import { useState } from "react";
import { ScopeType } from "sockapi/scope-types";
import { SockAPIScopeState } from "sockapi/scope-state";
import { DirectBackendReqType } from "sockapi/direct-backend";

import settings from "settings";
import { PuzzlePageWrapper } from "pages/PuzzlePage";
import {
  usePuzzleDisplayName,
  useErrataContentForPuzzle,
} from "stores/ScopeStateStore";
import {
  useSockClient,
  useShouldDisplayAdmin,
} from "stores/ServerInteractionStore";
import { PuzzleLink, Link } from "components/Link";
import { Spinner } from "components/Loading";
import { Paper } from "components/Material";
import { LongDate } from "components/dates";
import { useUILock } from "client-util/useUILock";

function ErratumLink({ puzName }: { puzName: string | null }) {
  const displayName = usePuzzleDisplayName(puzName);
  if (puzName === null || displayName === null) return null;
  return <PuzzleLink puzName={puzName}>{displayName}</PuzzleLink>;
}

function ErratumDisplay(props: {
  content: SockAPIScopeState<ScopeType.ERRATUM_CONTENT>;
  page: "updates" | "puzzle";
}) {
  const {
    content: { puzName, updatesText, puzzleText, timestamp },
    page,
  } = props;
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const [emails, setEmails] = useState<string[] | null>(null);
  const getEmailsLock = useUILock();
  const sockClient = useSockClient();
  const text: string = (() => {
    switch (page) {
      case "updates":
        return updatesText;
      case "puzzle":
        return puzzleText;
    }
  })();
  if (!text) return null;
  return (
    <Paper seed={text}>
      <div>
        On <LongDate timestamp={timestamp} />,{" "}
        {text
          .split(/(\$PUZZLE)/g)
          .map((segment, i) =>
            segment === "$PUZZLE" ? (
              <ErratumLink key={i} puzName={puzName} />
            ) : (
              <span key={i} dangerouslySetInnerHTML={{ __html: segment }} />
            )
          )}
      </div>
      {shouldDisplayAdmin && puzName !== null ? (
        <div>
          <button
            onClick={() => {
              getEmailsLock.acquireAndRun(async (getIsCanceled) => {
                try {
                  const emails = (
                    await sockClient.sendDirectBackendReqAsync({
                      type: DirectBackendReqType.GET_ERRATUM_EMAILS,
                      payload: { puzName },
                    })
                  ).payload;
                  if (getIsCanceled()) return;
                  setEmails(emails);
                } catch (err) {
                  console.error(err);
                  toastr.error("Failed to get emails.");
                }
              });
            }}
            disabled={getEmailsLock.isHeld}
          >
            Get emails
          </button>
        </div>
      ) : null}
      {emails !== null ? (
        emails.length === 0 ? (
          <div>None found.</div>
        ) : (
          <textarea>{emails.join(", ")}</textarea>
        )
      ) : null}
    </Paper>
  );
}

export function ErrataDisplay(props: {
  contents: {
    erratumId: string;
    content: SockAPIScopeState<ScopeType.ERRATUM_CONTENT>;
  }[];
}) {
  const { contents } = props;
  return contents.map(({ erratumId, content }) => (
    <ErratumDisplay key={erratumId} content={content} page="puzzle" />
  ));
}

function ErrataPage() {
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const contents = useErrataContentForPuzzle(null, true);
  if (contents === null) return <Spinner inline={true} />;
  return (
    <PuzzlePageWrapper>
      <div className="centered-page">
        <h1>
          Errata / Updates
          {!settings.isPosthunt && shouldDisplayAdmin ? (
            <div className="button-squish-zone">
              <Link
                route="admin_subpage"
                params={{ "*": "puzzles/erratum" }}
                button={true}
              >
                Edit errata
              </Link>
              <Link
                route="admin_subpage"
                params={{ "*": "puzzles/erratum/add" }}
                button={true}
              >
                Add new erratum
              </Link>
            </div>
          ) : null}
        </h1>
        {contents.map(({ erratumId, content }) => (
          <ErratumDisplay key={erratumId} content={content} page="updates" />
        ))}
      </div>
    </PuzzlePageWrapper>
  );
}

export default ErrataPage;
